// Form nhap tham so duoc SINH RA tu mo ta buoc ma backend tra ve.
// Them tham so moi cho script chi can sua pipeline.py, man hinh nay khong doi.

import { useEffect, useState } from "react";
import { api } from "../api";
import type { DataFile, Param, Step } from "../types";
import { Switch } from "./ui";

export type ParamValues = Record<string, unknown>;

/** Gia tri khoi tao lay tu "default" trong mo ta buoc. */
export function macDinh(step: Step): ParamValues {
  const v: ParamValues = {};
  for (const p of step.params) {
    if (p.type === "bool") v[p.name] = p.default ?? false;
    else if (p.type === "list") v[p.name] = (p.default as string[]) ?? [];
    else v[p.name] = p.default ?? "";
  }
  return v;
}

function FileChon({
  param,
  value,
  onChange,
}: {
  param: Param;
  value: string;
  onChange: (v: string) => void;
}) {
  const [files, setFiles] = useState<DataFile[] | null>(null);
  const [loi, setLoi] = useState<string | null>(null);

  const nap = () => {
    if (!param.source) return;
    api
      .files(param.source)
      .then((r) => {
        const loc = param.ext?.length
          ? r.files.filter((f) => param.ext!.some((e) => f.name.toLowerCase().endsWith(e)))
          : r.files;
        setFiles(loc);
        setLoi(null);
      })
      .catch((e) => setLoi(e instanceof Error ? e.message : String(e)));
  };

  useEffect(nap, [param.source]);

  // File dang duoc dung: chon tay, hoac file moi nhat khi de trong.
  const dangDung = value ? files?.find((f) => f.name === value) : files?.[0];

  return (
    <div>
      <div className="row" style={{ gap: 6 }}>
        <select className="select grow" value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">— Tự lấy file mới nhất —</option>
          {files?.map((f) => (
            <option key={f.name} value={f.name}>
              {f.name} · {f.mtime}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn-sm"
          onClick={nap}
          title="Nạp lại danh sách file"
        >
          ↻
        </button>
      </div>
      {loi ? (
        <div className="small" style={{ color: "var(--danger)", marginTop: 4 }}>{loi}</div>
      ) : null}
      {dangDung?.sub_class ? (
        <div className="hint">
          Sub-class suy từ tên file:{" "}
          <strong style={{ color: "var(--text)" }}>{dangDung.sub_class}</strong> — phải
          khớp đúng một mục trong dropdown Sub-class của panel, không thì mọi dòng đều hỏng.
          Sai thì điền ô “Ép Sub-class” ở phần nâng cao.
        </div>
      ) : null}
    </div>
  );
}

function DanhSach({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const dong = value.length ? value : [""];
  const sua = (i: number, v: string) => {
    const moi = [...dong];
    moi[i] = v;
    onChange(moi);
  };
  return (
    <div className="col" style={{ gap: 6 }}>
      {dong.map((d, i) => (
        <div className="row" key={i} style={{ gap: 6 }}>
          <input
            className="input grow"
            value={d}
            onChange={(e) => sua(i, e.target.value)}
            placeholder="CỘT=GIÁ TRỊ"
          />
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => onChange(dong.filter((_, j) => j !== i))}
            disabled={dong.length === 1 && !dong[0]}
            title="Xoá dòng"
          >
            ✕
          </button>
        </div>
      ))}
      <button type="button" className="btn btn-sm" onClick={() => onChange([...dong, ""])}>
        + Thêm điều kiện
      </button>
    </div>
  );
}

function MotThamSo({
  param,
  value,
  onChange,
}: {
  param: Param;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const full = param.type === "list" || param.type === "file" || param.name === "url";

  if (param.type === "bool") {
    return (
      <div className={`field ${full ? "param-full" : ""}`}>
        <Switch
          checked={Boolean(value)}
          onChange={onChange}
          label={
            <>
              {param.label}
              {param.danger ? " ⚠" : ""}
            </>
          }
        />
        {param.help ? <div className="hint">{param.help}</div> : null}
      </div>
    );
  }

  return (
    <div className={`field ${full ? "param-full" : ""}`}>
      <label className="label">
        {param.required ? <span className="req">*</span> : null}
        {param.label}
      </label>

      {param.type === "select" ? (
        <select
          className="select"
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
        >
          {param.options?.map((o) => (
            <option key={o} value={o}>
              {o === "" ? "— mặc định —" : o}
            </option>
          ))}
        </select>
      ) : param.type === "file" ? (
        <FileChon param={param} value={String(value ?? "")} onChange={onChange} />
      ) : param.type === "list" ? (
        <DanhSach value={(value as string[]) ?? []} onChange={onChange} />
      ) : (
        <input
          className="input"
          type={param.type === "number" ? "number" : "text"}
          value={String(value ?? "")}
          placeholder={param.default != null ? String(param.default) : ""}
          onChange={(e) => onChange(e.target.value)}
        />
      )}

      {param.help ? <div className="hint">{param.help}</div> : null}
    </div>
  );
}

export default function ParamForm({
  step,
  values,
  onChange,
}: {
  step: Step;
  values: ParamValues;
  onChange: (v: ParamValues) => void;
}) {
  const [moRong, setMoRong] = useState(false);
  const dat = (name: string, v: unknown) => onChange({ ...values, [name]: v });

  const thuong = step.params.filter((p) => !p.advanced && !p.danger);
  const nangCao = step.params.filter((p) => p.advanced && !p.danger);

  return (
    <div>
      <div className="param-grid">
        {thuong.map((p) => (
          <MotThamSo key={p.name} param={p} value={values[p.name]} onChange={(v) => dat(p.name, v)} />
        ))}
      </div>

      {nangCao.length > 0 && (
        <>
          <button type="button" className="adv-toggle" onClick={() => setMoRong(!moRong)}>
            {moRong ? "▾" : "▸"} Tuỳ chọn nâng cao ({nangCao.length})
          </button>
          {moRong && (
            <div className="param-grid">
              {nangCao.map((p) => (
                <MotThamSo
                  key={p.name}
                  param={p}
                  value={values[p.name]}
                  onChange={(v) => dat(p.name, v)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
